import React, { useState } from "react";
import { X, Upload, Loader2, Camera, AlertCircle } from "lucide-react";
import { api, formatErr } from "../../lib/api";
import { toast } from "sonner";

/**
 * AddTripPhotoModal — iter188
 *
 * Lets the OWNER of a trip story attach a new photo AFTER the trip was
 * already posted. Mirrors the photo-upload flow in NewStoryModal but
 * scoped to a single image so it's fast on mobile.
 *
 * Backend: POST /api/wall/{story_id}/photo — auth, owner-only.
 * Props:
 *   story: the trip story (needs story_id + campsite_name + campsite_id)
 *   onClose(): close modal
 *   onUpdated(newStory): swap the story in the parent list with the fresh one
 */
const MAX_BYTES = 5 * 1024 * 1024;

async function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    if (file.size > MAX_BYTES) return reject(new Error("Image too large (max 5MB)"));
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      try {
        const MAX = 1600;
        const ratio = Math.min(1, MAX / Math.max(img.width, img.height));
        const w = Math.round(img.width * ratio);
        const h = Math.round(img.height * ratio);
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        URL.revokeObjectURL(url);
        resolve(canvas.toDataURL("image/jpeg", 0.85));
      } catch (e) { reject(e); }
    };
    img.onerror = () => reject(new Error("Could not read image"));
    img.src = url;
  });
}

export default function AddTripPhotoModal({ story, onClose, onUpdated }) {
  const [dataUrl, setDataUrl] = useState(null);
  const [caption, setCaption] = useState("");
  const [useForCampsite, setUseForCampsite] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const remaining = 4 - ((story.photos || []).length);
  if (remaining <= 0) {
    // Shouldn't be reachable — parent hides button — but guard anyway.
    return null;
  }

  const onPickFile = async (file) => {
    try {
      const url = await fileToDataUrl(file);
      setDataUrl(url);
    } catch (e) {
      toast.error(e.message || "Could not read image");
    }
  };

  const submit = async () => {
    if (!dataUrl) return toast.info("Pick a photo first");
    setSubmitting(true);
    try {
      const { data } = await api.post(`/wall/${story.story_id}/photo`, {
        image_data_url: dataUrl,
        caption: caption.trim() || null,
        use_for_campsite: !!useForCampsite,
      });
      toast.success(
        useForCampsite
          ? `Photo added · sent to ${story.campsite_name || "the campsite"} for review.`
          : "Photo added to your trip."
      );
      onUpdated?.(data.story);
      onClose?.();
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6"
      role="dialog"
      aria-modal="true"
      data-testid="add-trip-photo-modal"
      onClick={onClose}
    >
      <div
        className="bg-white w-full sm:max-w-lg max-h-[95vh] overflow-y-auto border-2 hairline-strong shadow-[0_8px_0_0_#111]"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="sticky top-0 bg-white border-b hairline px-5 py-3 flex items-center justify-between z-10">
          <h2 className="font-display text-lg">Add a photo to this trip</h2>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-[#f5f5f5]"
            aria-label="Close"
            data-testid="add-trip-photo-close"
          >
            <X className="w-4 h-4" strokeWidth={2} />
          </button>
        </header>

        <div className="px-5 py-4 space-y-4">
          <div className="text-xs mono text-[#666]">
            Adding to <strong className="text-[#0e0e0e]">{story.campsite_name || "trip"}</strong> · {remaining} of 4 photo slot{remaining === 1 ? "" : "s"} left.
          </div>

          {/* Photo picker */}
          {dataUrl ? (
            <div className="relative border hairline overflow-hidden bg-[#111]">
              <img
                src={dataUrl}
                alt="Selected"
                className="w-full max-h-[320px] object-contain bg-[#111]"
                data-testid="add-trip-photo-preview"
              />
              <button
                onClick={() => setDataUrl(null)}
                className="absolute top-2 right-2 bg-black/70 text-white px-2 py-1 text-[11px] mono uppercase tracking-wider hover:bg-black"
                data-testid="add-trip-photo-clear"
              >
                Change photo
              </button>
            </div>
          ) : (
            <label
              className="block aspect-[4/3] border-2 border-dashed hairline-strong flex flex-col items-center justify-center gap-2 text-sm mono text-[#666] cursor-pointer hover:bg-[#fff4ee]"
              data-testid="add-trip-photo-picker"
            >
              <Upload className="w-6 h-6 text-[#ff4500]" strokeWidth={1.75} />
              Tap to choose a photo
              <span className="text-[10px] text-[#888]">JPG / PNG · max 5MB</span>
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => e.target.files?.[0] && onPickFile(e.target.files[0])}
              />
            </label>
          )}

          {/* Optional caption */}
          <div>
            <label className="eyebrow block mb-1.5">Caption (optional)</label>
            <input
              type="text"
              value={caption}
              onChange={(e) => setCaption(e.target.value.slice(0, 160))}
              placeholder="A short line to go with this photo…"
              className="w-full border hairline px-3 py-2 text-sm"
              data-testid="add-trip-photo-caption"
            />
            <div className="text-[10px] mono text-[#888] text-right mt-1">{caption.length}/160</div>
          </div>

          {/* Share with campsite */}
          {story.campsite_id && story.campsite_id !== "general" && (
            <div className="border-2 border-[#ff4500] bg-[#fff4ee] p-3">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={useForCampsite}
                  onChange={(e) => setUseForCampsite(e.target.checked)}
                  className="mt-1 w-4 h-4 accent-[#ff4500] flex-shrink-0"
                  data-testid="add-trip-photo-use-for-campsite"
                />
                <span className="flex-1">
                  <span className="flex items-center gap-1.5 mb-1">
                    <Camera className="w-3.5 h-3.5 text-[#ff4500]" strokeWidth={2} />
                    <strong className="text-sm">Also add this photo to {story.campsite_name || "the campsite"}'s gallery</strong>
                  </span>
                  <span className="block text-[11px] mono leading-snug text-[#5a3520]">
                    We'll send it to our moderator queue and append it to the campsite's photo gallery after a quick review. Landscapes &amp; surroundings only — please don't include people.
                  </span>
                </span>
              </label>
            </div>
          )}
        </div>

        <footer className="sticky bottom-0 bg-white border-t hairline px-5 py-3 flex items-center justify-between gap-3">
          <button
            onClick={onClose}
            className="text-sm mono underline text-[#666]"
            data-testid="add-trip-photo-cancel"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={submitting || !dataUrl}
            className="btn-primary text-xs px-5 py-2.5 inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="add-trip-photo-submit"
          >
            {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <AlertCircle className="w-3.5 h-3.5" strokeWidth={2} />}
            {submitting ? "Adding…" : "Add photo"}
          </button>
        </footer>
      </div>
    </div>
  );
}
